import { Button, Field, Icon, Input, RadioButton, RadioButtonGroup, Select } from "@deligoo/ui"
import { IconName } from "@deligoo/ui/src/icons/build"
import { FormEvent, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import NumberFormat from "react-number-format"
import { toast } from "react-toastify"

import { useAppSelector } from "@/App"
import { Card } from "@/components/Card"
import { selectDrivers } from "@/store/drivers"
import { formatPrice } from "@/utils"

import { useManualOrderFormContext } from "../AddManualOrderView"
import cls from "../AddManualOrderView.module.scss"
import { ManualOrderFormData } from "../useManualOrderForm"

export const ParametersView = () => {
  const drivers = useAppSelector(selectDrivers)

  const { formState, setFormStateValue, setNextStep, setPreviousStep } = useManualOrderFormContext()

  const [totalPrice, setTotalPrice] = useState<number | null>(null)

  useEffect(() => {
    const { price_delivery_subunit, price_subunit } = formState.values
    if (price_subunit !== undefined && price_delivery_subunit !== undefined) {
      setTotalPrice(price_subunit + price_delivery_subunit)
    } else {
      setTotalPrice(null)
    }
  }, [formState.values])

  useEffect(() => {
    const { payment_form, price_subunit, price_delivery_subunit } = formState.values

    if (payment_form !== "paid") return

    if (price_subunit === undefined) setFormStateValue({ price_subunit: 0 })
    if (price_delivery_subunit === undefined) setFormStateValue({ price_delivery_subunit: 0 })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.values.payment_form])

  function submitStep() {
    const { payment_form, price_subunit, price_delivery_subunit } = formState.values || {}

    let errorsCount = 0

    toast.dismiss("paymentFormError")
    toast.dismiss("priceError")

    if (!payment_form) {
      errorsCount++

      toast.error("Wybierz formę płatności", {
        toastId: "paymentFormError",
        updateId: "paymentFormError",
      })
    }

    if (
      price_subunit === undefined ||
      price_delivery_subunit === undefined ||
      (payment_form !== "paid" && !price_subunit)
    ) {
      errorsCount++

      toast.error("Uzupełnij koszty zamówienia", {
        toastId: "priceError",
        updateId: "priceError",
      })
    }

    if (!errorsCount) setNextStep()
  }

  return (
    <Card className={cls.card}>
      <Card.Section>
        <h3>Wprowadź parametry</h3>
      </Card.Section>

      <Card.Section>
        <Field label="Kierowca (opcjonalnie)" name="proposed_driver_id" className={cls.fullWidth}>
          <Select
            id="proposed_driver_id"
            onChange={(e) => setFormStateValue({ proposed_driver_id: Number(e.currentTarget.value) })}
          >
            <option />

            {drivers.map((driver) => (
              <option value={driver.id} key={driver.id}>
                {driver.name} ({driver.is_available ? "aktywny" : "nieaktywny"})
              </option>
            ))}
          </Select>
        </Field>

        {/* <h2 className="py-8">Pojazd</h2>
        <RadioButtonGroup>
          <TransportType transportType="car" />
          <TransportType transportType="bicycle" />
          <TransportType transportType="scooter" />
        </RadioButtonGroup> */}

        <h2 className="py-8">Forma płatności</h2>

        <RadioButtonGroup>
          <PaymentForm paymentForm="card" />
          <PaymentForm paymentForm="cash" />
          <PaymentForm paymentForm="paid" />
        </RadioButtonGroup>

        <h2 className="py-8">Kwoty zamówienia</h2>

        <div className={cls.prices}>
          <PriceInputField
            label="Kwota zamówienia"
            name="price_subunit"
            value={formState.values.price_subunit}
            setValue={(value: string) => setFormStateValue({ price_subunit: priceToSubunit(value) })}
          />

          <PriceInputField
            label="Kwota dowozu"
            name="price_delivery"
            value={formState.values.price_delivery_subunit}
            setValue={(value: string) => setFormStateValue({ price_delivery_subunit: priceToSubunit(value) })}
          />

          <Field
            label="Suma"
            inputProps={{ disabled: true, defaultValue: formatPrice(totalPrice, { subunit: true }) }}
          />
        </div>
      </Card.Section>

      <Card.Section className={cls.submitBtns}>
        <Button
          className={cls.submitBtn}
          color="primary"
          variation="accented"
          size="extra-large"
          fullWidth
          onClick={setPreviousStep}
        >
          <Icon name="short-arrow-tiny-left" />
          Wstecz
        </Button>

        <Button className={cls.submitBtn} color="primary" size="extra-large" fullWidth onClick={submitStep}>
          Przejdź dalej
          <Icon name="short-arrow-tiny-right" />
        </Button>
      </Card.Section>
    </Card>
  )
}

function priceToSubunit(value?: string | number) {
  if (value === undefined || value === "") return undefined

  return Number(value) * 100
}

type PriceInputFieldProps = {
  label: string
  name: string
  value?: string | number
  setValue: (value: string) => void
}

const PriceInputField = ({ label, name, value, setValue }: PriceInputFieldProps) => {
  return (
    <Field label={label} name={name}>
      <NumberFormat
        id={name}
        name={name}
        customInput={Input}
        value={value === undefined || value === "" ? "" : formatPrice(value, { subunit: true })}
        onChange={(e: FormEvent<HTMLInputElement>) => setValue(e.currentTarget.value)}
        onBlur={(e: FormEvent<HTMLInputElement>) => {
          e.currentTarget.value = formatPrice(e.currentTarget.value)
        }}
        size="large"
        maxLength={10}
        decimalScale={2}
        allowNegative={false}
        allowedDecimalSeparators={[".", ","]}
        isNumericString
        selectOnFocus
        plain
      />
    </Field>
  )
}

type PaymentFormProps = {
  paymentForm: NonNullable<ManualOrderFormData["values"]["payment_form"]>
}

const PaymentForm = ({ paymentForm }: PaymentFormProps) => {
  const { formState, setFormStateValue } = useManualOrderFormContext() || {}

  const { t } = useTranslation("orders")

  if (!setFormStateValue) return null

  const checked = formState?.values?.payment_form === paymentForm

  return (
    <RadioButton
      id={paymentForm}
      checked={checked}
      onChange={() => setFormStateValue({ payment_form: paymentForm })}
      iconName={getPaymentFormIconName(paymentForm)}
      required
    >
      {t(paymentForm)}
    </RadioButton>
  )
}

function getPaymentFormIconName(paymentForm: NonNullable<ManualOrderFormData["values"]["payment_form"]>): IconName {
  switch (paymentForm) {
    case "card":
      return "credit-card"
    case "cash":
      return "cash"
    case "paid":
      return "check-circle"
  }
}

// type TransportTypeProps = {
//   transportType: NonNullable<ManualOrderFormData["transport_type"]>
// }

// const TransportType = ({ transportType }: TransportTypeProps) => {
//   const { formState, setFormStateValue } = useManualOrderFormContext() || {}
//   const { t } = useTranslation("drivers")

//   if (!setFormStateValue) return null

//   const checked = formState?.transport_type === transportType

//   return (
//     <RadioButton
//       id={transportType}
//       checked={checked}
//       onChange={() => setFormStateValue({ transport_type: transportType })}
//       iconName={getTransportTypeIconName(transportType)}
//       required
//     >
//       {t(`${transportType}`)}
//     </RadioButton>
//   )
// }

// function getTransportTypeIconName(transportType: NonNullable<ManualOrderFormData["transport_type"]>): IconName {
//   switch (transportType) {
//     case "car":
//       return "private-car"
//     case "bicycle":
//       return "bike"
//     case "scooter":
//       return "scooter"
//   }
// }
